// Encapsulates a session/clip expression and makes it easy to query marker times efficiently.
// format: ([userid!sessionid([pageid([time:]+):]+):]+)
import type { PageModel } from '../models/Page';
import { pageKey } from './pageKey';
import { SessionModel } from '../models/Session';
import { userSessionId } from './userSessionId';
import { emitSExpr, S } from '@fullstory/util/sexpr';
import { EventMarker } from '@fullstory/proto/fs/services/alexa';
import { int64 } from '@fullstory/proto/google/protobuf';
import { clamp, toNumber } from 'lodash';

type PageClip = {
  pageId: string;
  times: (number | string)[];
};

type SessionClip = {
  sessionKey: string | undefined; // session key can be a session Id or a combination of session Id & userId
  pages: PageClip[];
};

type RequestedStartData = {
  absStartTime: number;
  pageOffset?: number;
  pageId?: string;
  linearizationState?: 0 | 1 | 2;
};

type ParsedSExpr = {
  individualId?: string;
  requestedStartData?: RequestedStartData;
  noteId?: string;
} & (
  | { sessionType: 'single'; singleSessionId: string }
  | { sessionType: 'sessionHighlights'; sessionClips: SessionClip[] }
  | { sessionType: 'error' }
);

export class SessionExpr {
  // We need to restrict the length of the Session URL to 2048 characters, to be compatible with all browsers
  // The Session URL can be broken into Prefix + Session Expr (IDs and event markers, output of emitSExpr)
  // The prefix would look like 'https://app.staging.fullstory.com/ui/thefullstory.com/session/' (with 62 character ~ 65 characters)
  // To be conservative, we are choosing the longest host + OrgID combination in the URL
  // 2048 - 65 = 1983
  static MaxSessionExprLength: number = 1983;

  private _highlightedAbsTimes: number[] = [];

  // A map from absolute timestamps to the corresponding page within the session
  // or clip.
  private _absTimeToPage = new Map<number, Readonly<PageModel>>();

  private _indivId: string | undefined;
  private _singleSessionId: string | undefined; // Either this or _sessions will be set, but not both.
  private _clipSessions: SessionClip[] | undefined;
  private _requestedStartTime: number | undefined; // Absolute time in Millis.
  private _noteId: string | undefined; // ID of the note to preview.
  private _requestedPageId: string | undefined;

  // Returns the formatted state to be used by this pane's history state parser.
  // Note that this assumes that all markers belong to the same user.
  static markersHistoryState(indivId: string, markers: EventMarker[]): string {
    const expr = SessionExpr.fromMarkers(indivId, markers);
    return emitSExpr(expr.sexpr());
  }

  // Returns the formatted state to be used by this pane's history state parser.
  static historyState(indivId: string, userSessionId: string, startTime: number = 0): string {
    return `${indivId}:${userSessionId}${startTime ? `:${startTime}` : ''}`;
  }

  // Special case used for temporary parsing -- this will produce a SessionExpr without page times filled in.
  static fromSExpr(sexpr: S[]): SessionExpr {
    // NOTE: it doesn't matter what we set for the `linearized` argument because there will be no page times.
    return SessionExpr.fromModel(null, sexpr);
  }

  get isClip(): boolean {
    return !this.isSession() && !this.isSingleSession();
  }

  private _parseSessionId(sessionDataExpr: S | undefined) {
    if (!sessionDataExpr || !sessionDataExpr.value || sessionDataExpr.kids) {
      // sanity check bc if we have kids here then it is not a single session
      return;
    }

    return sessionDataExpr.value;
  }

  /**
   * Parses requested startData from s-expression S[2]
   * S-expression structure: indivId:SessionData:RequestedStartData:NoteId/
   *
   * RequestedStartData Examples:
   * EX: 1 (legacy url with a requested start time)
   *  S-expression RequestedStartData string: "1677171000819"
   *  S-Expr: {value:1677171000819                   } // no kids
   *          {value:Session Requested abs Start Time}
   *
   * EX:2 (url with request startData)
   *  S-expression RequestedStartData string: "1677171013371(6617:93463)"
   *  S-Expr: {value:1677171013371,                    kids:[{value:6617},   {value: 93463},                      ]}
   *          {value:Session Requested abs Start Time, kids:[{value:PageId}, {value: # of ms from page startTime} ]}
   *
   * EX:3 (temporay transition expression)
   *  S-expression RequestedStartData string: "1677171013371(6617:93463:0)"
   *  S-Expr: {value:1677171000819,                    kids:[{value:6617},   {value: 93463},                       {value: 0                                                         } ]}
   *          {value:Session Requested abs Start Time, kids:[{value:PageId}, {value: # of ms from page startTime}, {value: Linearization State when shareLink was created (0 | 1 | 2)} ]}
   */
  private _parseRequestedStartData(requestedStartTimeSexpr: S | undefined) {
    if (!requestedStartTimeSexpr || !requestedStartTimeSexpr.value) {
      // sanity check
      return;
    }
    const requestedStartData: RequestedStartData = {
      absStartTime: toNumber(requestedStartTimeSexpr.value),
    };

    if (requestedStartTimeSexpr.kids?.[0]?.value) {
      requestedStartData.pageId = requestedStartTimeSexpr.kids[0].value;
    }

    if (requestedStartTimeSexpr.kids?.[1]?.value !== undefined) {
      requestedStartData.pageOffset = toNumber(requestedStartTimeSexpr.kids[1].value);
    }

    if (requestedStartTimeSexpr.kids?.[2]?.value !== undefined) {
      const maybeLinearizationState = toNumber(requestedStartTimeSexpr.kids[2].value);
      requestedStartData.linearizationState = clamp(maybeLinearizationState, 0, 2) as 0 | 1 | 2;
    }

    return requestedStartData;
  }

  /**
   * Parses session clips from s-expression S[1]
   * S-expression structure: indivId:SessionData:RequestedStartData:NoteId/
   *
   * SessionData clip example:
   * S-expression string: "(4082677445835355299!7719592479781903255(539558939424614424(8137)))"
   *  S-Expr: {value: undefined, kids: [{value: 4082677445835355299!7719592479781903255, kids: [{value: 539558939424614424, kids: [{value: 8137     }] }] }]
   *          {value: undefined, kids: [{value: userId!sessionId,                        kids: [{value: pageId,             kids: [{value: ms offset}] }] }] }]
   **/
  private _parseSessionClip(clipExpr: S | undefined) {
    if (!clipExpr || !clipExpr.kids) {
      // sanity check
      return;
    }

    const sessionClips: SessionClip[] = [];
    for (const sessionExpr of clipExpr.kids) {
      if (!sessionExpr?.value) {
        // every session for have an id
        continue;
      }
      const sessionClip: SessionClip = {
        sessionKey: sessionExpr.value,
        pages: [],
      };
      sessionClips.push(sessionClip);

      for (const pageExpr of sessionExpr.kids ?? []) {
        if (!pageExpr.value) {
          continue;
        }
        const pageClip: PageClip = { pageId: pageExpr.value, times: [] };
        sessionClip.pages.push(pageClip);

        for (const pageOffsetExpr of pageExpr.kids ?? []) {
          const pageOffset = pageOffsetExpr?.value ? toNumber(pageOffsetExpr.value) : 0;
          pageClip.times.push(pageOffset);
        }
      }
    }

    return sessionClips;
  }

  /**
   * Parse known s-expresion into know values.
   *
   * NOTE: trying to create a structure example : this might not be complete or cover all know cases
   *   - S-Expr[0] : indivId (maybe userId if some cases ... it unclear)
   *   - S-Expr[1] : session identifier(s)
   *      - if has no kids : session Id
   *      - else has kids  : clips url with list of session Ids & Pages
   *   - S-Expr[2] : requested start time **OPTIONAL**
   *       - if has no kids : requested start time (an absolute request time that for the session it start at. It is a "request" time b/c we need to valid it)
   *       - else has kids  : requested session start time as parent, with three kids, first kid is the page abs start time, the second kid is # of ms after page start time, third is it page Id
   *   - S-Expr[3] : noteId that is being linked. **OPTIONAL**
   *
   * EX: 1 (legacy url with a requested start time)
   *  S-expression string: 4895:6616:1677171000819:5139941981945856/
   *  S-Expr: [{value:4895},    {value:6616},      {value:1677171000819},                    {value: 5139941981945856}] // no kids
   *          [{value:indivId}, {value:sessionId}, {value:Session Requested abs Start Time}, {value: NoteId}          ] // noteId is option here
   *
   * EX:2 (url with request startData)
   *  S-expression string: 4895:6616:1677171013371(6617:93463):5817241144655872/
   *  S-Expr: [{value:4895}, {value:6616}, {value:1677171000819, kids:[{value:6617}, {value: 93463}]}, {value: 5139941981945856}]
   *   - This is the exact same as 'EX 1' beside the request start time has kids.
   *   - {value:1677171000819,                    kids:[{value:6617},   {value: 93463},                      ]}
   *   - {value:Session Requested abs Start Time, kids:[{value:PageId}, {value: # of ms from page startTime} ]}
   *
   * EX:3 (temporay transition expression)
   *  S-expression string: 4895:6616:1677171013371(6617:93463:0):5817241144655872/
   *  S-Expr: [{value:4895}, {value:6616}, {value:1677171000819, kids:[{value:6617}, {value: 93463}, {value: 0}]}, {value: 5139941981945856}]
   *   - This is the exact same as 'EX 1' beside the request start time has kids.
   *   - {value:1677171000819,                    kids:[{value:6617},   {value: 93463},                       {value: 0}      ]}
   *   - {value:Session Requested abs Start Time, kids:[{value:PageId}, {value: # of ms from page startTime}, {value: Linearization State when shareLink was created}   ]}
   *
   * EX: 4 (clips)
   *  S-expression string: 4082677445835355299:(4082677445835355299!7719592479781903255(539558939424614424(8137)))
   *  S-Expr: [{value: 4082677445835355299}, {value: undefined, kids: [{value: 4082677445835355299!7719592479781903255, kids: [{value: 539558939424614424, kids: [{value: 8137     }] }] }] }]
   *          [{value: individualId         }, {value: undefined, kids: [{value: userId             !sessionId,           kids: [{value: pageId,             kids: [{value: ms offset}] }] }] }]
   **/
  private _parseSexpr(sexpr: S[]): ParsedSExpr {
    if (!sexpr) {
      // sanity check
      return { sessionType: 'error' };
    }

    const individualId = sexpr[0]?.value;
    const maybeSingleSessionId = this._parseSessionId(sexpr[1]);
    const maybeSessionClips = this._parseSessionClip(sexpr[1]);
    const requestedStartData = this._parseRequestedStartData(sexpr[2]);
    const noteId = sexpr[3]?.value ? sexpr[3].value : undefined;

    if (maybeSingleSessionId) {
      return {
        individualId,
        sessionType: 'single',
        singleSessionId: maybeSingleSessionId,
        requestedStartData,
        noteId,
      };
    }

    if (maybeSessionClips) {
      return {
        individualId,
        sessionType: 'sessionHighlights',
        sessionClips: maybeSessionClips,
        requestedStartData,
        noteId,
      };
    }

    return {
      individualId,
      sessionType: 'error',
      requestedStartData,
      noteId,
    };
  }

  private _setHighlights(model: SessionModel | null) {
    if (!model || !this._clipSessions) {
      // sanity check
      return;
    }

    for (const sessionClip of this._clipSessions) {
      for (const pageClip of sessionClip.pages) {
        for (const pageOffset of pageClip.times) {
          const { userId, sessionId, err } = parseSessionAndUserId(sessionClip.sessionKey);
          if (err != null) {
            console.error(err);
            continue;
          }

          const page = model.pageByKey(pageKey(userId, sessionId, pageClip.pageId));
          if (!page) {
            continue;
          }
          const absTime = page.localStartTime + toNumber(pageOffset);
          this._highlightedAbsTimes.push(absTime);
          this._absTimeToPage.set(absTime, page);
        }
      }
    }
    // Sort markers. They can get out of order in the case of overlapping pages.
    this._highlightedAbsTimes.sort((a, b) => Math.sign(a - b));
  }

  private _getRequestedStartTime(
    model: SessionModel | null,
    maybeRequestedStartData: RequestedStartData | undefined,
  ) {
    if (!maybeRequestedStartData) {
      // sanity check
      return;
    }

    if (!model) {
      // legacy compatibility since methods like static fromSExpr(sexpr: S[]): SessionExpr { exists
      return maybeRequestedStartData.absStartTime;
    }

    const {
      absStartTime: requestedAbsStartTime,
      pageOffset: requestedPageOffset,
      pageId: requestedPageId,
      linearizationState,
    } = maybeRequestedStartData;
    const page = model.pageById(requestedPageId || '');
    const note = findNote(this._noteId || '', model.pages);
    this._requestedPageId = page?.pageId || note?.PageId;

    if (page && requestedPageOffset !== undefined && linearizationState === undefined) {
      // if we have the it is a new s-expression from a none-linearized session
      return page.localStartTime + requestedPageOffset;
    }

    if (note) {
      // if we have a noteId that is associated with a note then it absTime
      // should be correct and updated via the pageModel so that will be the
      // start time @Link pageModel updateNotesToNonLinearizedTime & updateNotesToLinearizedTime
      this._requestedPageId = note.PageId;
      return note.AbsTime;
    }

    if ((!this._noteId && !page) || linearizationState === 0) {
      // edge case for fs.js can get a playback link with a none-linearized absolute time.
      // if you are viewing it in a linearized experience then we need to account for that
      const noneLinearizedPage = model.currentPageAtTime(requestedAbsStartTime);
      if (noneLinearizedPage) {
        this._requestedPageId = this._requestedPageId || noneLinearizedPage.pageId;
        return requestedAbsStartTime;
      }
    }

    if (linearizationState || this._noteId) {
      // if linearized state flag or is it an old s-express with only an allocated noteId & is not
      // being viewed in a linearized experience then assume it was created in a linearized expereience.
      // We then have to find out what would be showing at this time and then de-linearize it
      const linerizedPageIdx = model.linearized.pageIndexAtTime(requestedAbsStartTime);
      const pageFromLinearizedModel = model.linearized.page(linerizedPageIdx);
      if (pageFromLinearizedModel) {
        this._requestedPageId = this._requestedPageId || pageFromLinearizedModel.pageId; // if we do not already have a requested pageID
        const pageOffset = requestedAbsStartTime - pageFromLinearizedModel.startTime;
        return pageFromLinearizedModel.localStartTime + pageOffset;
      }
    }

    return requestedAbsStartTime;
  }

  /**
   * Takes in s-expression object and returns a SessionExpr structure
   * which contains the correct interpretations of that data.
   *
   * Ex:
   *  - individualId
   *  - userIds
   *  - cliped session with pages & times or single sessionId
   *  - requested start time
   *  - requested noteId
   */
  static fromModel(model: SessionModel | null, sexpr: S[]): SessionExpr {
    const expr = new SessionExpr();
    const parsedSExpr = expr._parseSexpr(sexpr);
    const { individualId, requestedStartData, noteId } = parsedSExpr;
    expr._indivId = individualId;
    expr._noteId = noteId;
    expr._requestedStartTime = expr._getRequestedStartTime(model, requestedStartData);

    if (parsedSExpr.sessionType === 'error') {
      console.error('clip or sessionId expression missing from sexpr');
      return expr;
    }
    if (parsedSExpr.sessionType === 'single') {
      expr._singleSessionId = parsedSExpr.singleSessionId;
      // Just a single session.
      return expr;
    }

    expr._clipSessions = parsedSExpr.sessionClips;
    // setting highlights for clips
    expr._setHighlights(model);

    return expr;
  }

  static fromMarkers(indivId: string, markers: EventMarker[]): SessionExpr {
    const se = new SessionExpr();
    se._indivId = indivId;
    se._clipSessions = [];

    let curUserId = int64.nil,
      curSessionId = int64.nil,
      curPageId = int64.nil;
    let curSession: SessionClip | null = null,
      curPage: PageClip | null = null;

    // urlLengthEstimate keeps track of length of the session url
    let urlLengthEstimate = `${indivId}`.length + 2; // for '(' and ')'
    for (let i = 0; i < markers.length; ++i) {
      const m = markers[i]!;
      if (m.userId != curUserId || m.sessionId != curSessionId) {
        curUserId = m.userId;
        curSessionId = m.sessionId;
        curSession = { sessionKey: userSessionId(curUserId, curSessionId), pages: [] };
        se._clipSessions.push(curSession);
        curPage = null;
        curPageId = int64.nil;
        urlLengthEstimate += `${m.userId}`.length + `${m.sessionId}`.length + 3; // for '!', '(' and ')'
      }

      if (m.pageId != curPageId) {
        curPageId = m.pageId;
        curPage = { pageId: String(curPageId), times: [] };
        curSession?.pages.push(curPage);
        urlLengthEstimate += `${m.pageId}`.length + 2; // for '(' and ')'
      }

      urlLengthEstimate += `${m.pageOffset}`.length + 1; // for ':'
      if (urlLengthEstimate >= SessionExpr.MaxSessionExprLength) {
        // We will exceed the maximum allowed URL length if we encode any more markers
        // This has the effect of highlighting first 'i-1' matching events for the session
        console.warn('Restricting the number of matching events to ', i);
        break;
      }
      curPage?.times.push(`${m.pageOffset}`);
    }
    return se;
  }

  indivId(): string | undefined {
    return this._indivId;
  }

  requestedStartTime(): number {
    return this._requestedStartTime ? this._requestedStartTime : 0;
  }

  setRequestedStartTime(absTime: number) {
    this._requestedStartTime = absTime;
  }

  times(): number[] {
    return this._highlightedAbsTimes;
  }

  pages(): IterableIterator<Readonly<PageModel>> {
    return this._absTimeToPage.values();
  }

  noteId(): string | undefined {
    return this._noteId;
  }

  requestedPageId(): string | undefined {
    return this._requestedPageId;
  }

  // Returns an sexpr representing the entire session/clip.
  sexpr(): S[] {
    // Build up nested sessions/pages/times expression.
    const sexpr: S = {};
    if (this._singleSessionId) {
      sexpr.value = this._singleSessionId;
    } else {
      sexpr.kids = [];
      if (this._clipSessions) {
        for (let i = 0; i < this._clipSessions.length; ++i) {
          const session = this._clipSessions[i]!;
          const sessionExpr: S = { value: session.sessionKey, kids: [] };
          sexpr.kids.push(sessionExpr);

          for (let j = 0; j < session.pages.length; ++j) {
            const page = session.pages[j]!;
            const pageExpr: S = { value: page.pageId, kids: [] };
            sessionExpr.kids?.push(pageExpr);

            for (let k = 0; k < page.times.length; ++k) {
              pageExpr.kids?.push({ value: `${page.times[k]}` });
            }
          }
        }
      }
    }

    const r: S[] = [{ value: this._indivId }, sexpr];
    if (this._requestedStartTime) {
      r.push({ value: `${this._requestedStartTime}` });
    }
    return r;
  }

  // Returns an sexpr representing only the first session, with no pages/times.
  sexprSessionOnly(): S[] {
    const sexpr: S = {};
    if (this._singleSessionId) {
      sexpr.value = this._singleSessionId;
    } else {
      sexpr.value = this._clipSessions?.[0]?.sessionKey;
    }
    return [{ value: this._indivId }, sexpr];
  }

  firstMarkerForPage(page: Readonly<PageModel>): number {
    for (const time of this.times()) {
      if (this._absTimeToPage.get(time) === page) {
        return time;
      }
    }
    return page.startTime;
  }

  // Determines whether this expression designates a full session (as opposed to a clip).
  isSession(): boolean {
    return !this._highlightedAbsTimes.length;
  }

  // Determines whether this expression is a clip that is contained in a single session.
  isSingleSession(): boolean {
    return !!this._singleSessionId || this._clipSessions?.length == 1;
  }

  singleSessionId(): string | undefined {
    return this._singleSessionId;
  }

  hasTime(time: number): boolean {
    return this._absTimeToPage.has(time);
  }

  pageForTime(time: number): Readonly<PageModel> | undefined {
    return this._absTimeToPage.get(time);
  }

  eventCount(): number {
    return this._absTimeToPage.size;
  }

  /*
   * Test if we should take corrective action with respect a URL's start time.  The URL's start time is manifested
   * as a SessionExpr that is derived from the last bit of the URL.
   * This was the problematic type of URL https://app.staging.fullstory.com/ui/thefullstory.com/session/5614647586127872:4983894137176064:1602080974787
   * where the third value of the sexpr is before the start time of the session.
   */
  isTimeInBounds(startTime: number): boolean {
    return startTime < this.requestedStartTime();
  }
}

function findNote(id: string, pages: PageModel[] = []) {
  for (const page of pages) {
    for (const note of page.notes) {
      if (note.Id === id) return note;
    }
  }

  return undefined;
}

// Gets the userId and SessionId
function parseSessionAndUserId(sessionStr: string = ''): {
  userId: string;
  sessionId: string;
  err: Error | null;
} {
  let sessionId = '';
  let userId = '';
  const bangIdx = sessionStr.indexOf('!');
  let err: Error | null = null;
  if (bangIdx != -1) {
    sessionId = sessionStr.slice(bangIdx + 1);
    userId = sessionStr.slice(0, bangIdx);
  } else {
    err = new Error(`failed to parse userId and session Id out of ${sessionStr}; expected '!'`);
  }
  return {
    userId,
    sessionId,
    err,
  };
}
